import {
  Banner,
  Button,
  Sheet,
  SheetContent,
  SheetDescription,
  SheetHeader,
  SheetTitle,
  SheetTrigger,
  sonnerToast,
} from "@simplicity/ui";
import type { Connector } from "../../configTypes";

const REDACTED = "••••• (redacted)";
const SENSITIVE_KEY = /secret|password|token|key$/i;
// These end in "Key" but name columns or params; redacting them would hide the tables config.
const STRUCTURAL_KEYS = new Set(["primaryKey", "paramKey"]);

function isPlainObject(value: unknown): value is Record<string, unknown> {
  return value !== null && typeof value === "object" && !Array.isArray(value);
}

function redactEntry(key: string, value: unknown): unknown {
  // Keep the secret names (which references exist is useful); never their values.
  if (key === "secrets" && isPlainObject(value)) {
    return Object.fromEntries(Object.keys(value).map((k) => [k, REDACTED]));
  }
  if (SENSITIVE_KEY.test(key) && !STRUCTURAL_KEYS.has(key)) return REDACTED;
  return redactConfig(value);
}

function redactConfig(value: unknown): unknown {
  if (Array.isArray(value)) return value.map(redactConfig);
  if (!isPlainObject(value)) return value;
  return Object.fromEntries(Object.entries(value).map(([k, v]) => [k, redactEntry(k, v)]));
}

/** The connector config shape the runner receives (core's `baseConnectorConfigSchema`). */
function savedConfigJson(connector: Connector): string {
  const { id, name, type, prefix, vars, secrets } = connector;
  return JSON.stringify(redactConfig({ id, name, type, prefix, vars, secrets }), null, 2);
}

async function copyJson(json: string) {
  try {
    await navigator.clipboard.writeText(json);
    sonnerToast.success("Copied config JSON");
  } catch {
    sonnerToast.error("Couldn't copy. Select the JSON and copy it manually.");
  }
}

interface ConnectorConfigJsonSheetProps {
  /** The connector as last returned by the server, never the form's working copy. */
  connector: Connector;
  hasUnsavedChanges: boolean;
}

export function ConnectorConfigJsonSheet({
  connector,
  hasUnsavedChanges,
}: ConnectorConfigJsonSheetProps) {
  const json = savedConfigJson(connector);
  return (
    <Sheet>
      <SheetTrigger asChild>
        <Button type="button" variant="outline" size="sm">
          View config JSON
        </Button>
      </SheetTrigger>
      <SheetContent side="right" className="flex w-full flex-col gap-4 sm:max-w-2xl">
        <SheetHeader>
          <SheetTitle>Saved config (read-only)</SheetTitle>
          <SheetDescription>
            This connector&rsquo;s config as last saved on the server. Secret values are redacted.
          </SheetDescription>
        </SheetHeader>
        {hasUnsavedChanges && (
          <Banner variant="warning">
            This page has unsaved changes. They are not shown here until you save.
          </Banner>
        )}
        <div className="flex justify-end">
          <Button type="button" variant="outline" size="sm" onClick={() => void copyJson(json)}>
            Copy
          </Button>
        </div>
        <pre className="min-h-0 flex-1 overflow-auto rounded-md border border-border bg-muted/40 p-4 font-mono text-xs text-foreground">
          {json}
        </pre>
      </SheetContent>
    </Sheet>
  );
}
